import * as Consts from '../constants';
import {billingReportsDataParams, contractFilterParams} from '../helpers/utils';
import { t } from 'i18next';
import moment from 'moment';

const { keys } = Object;

// ================================
// REQUESTS
// ================================

export const getMetadata = async () => {
  const response = await fetch(`${process.env.REACT_APP_REPORTS_SERVICE_URL}/reportsmetadata`, {
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'GET',
  });

  if (! response.ok) throw response;
  const jsonResponse = await response.json();
  return jsonResponse;
};

const getReportsMetadata = async (accessToken, reportName) => {
  let response = null;

  const argString = [`report_name=${reportName}`];
  response = await fetch(`${process.env.REACT_APP_REPORTS_SERVICE_URL}/reportsmetadata?${argString}`, {
    headers: {
      Authorization: `auth_token ${accessToken}`,
      'Content-Type': 'application/json',
    },
    method: 'GET',
  });

  if (! response.ok) throw response;
  const jsonResponse = await response.json();
  return jsonResponse;
};

const MeteringApiReportNames = [
  'Contract_Datapoint_Transactions', 'Contract_Datapoint_Transactions_Over_Time',
  'Contract_Total_Transactions_Over_Time', 'Contract_Datastream_Transactions',
  'Contract_Datastream_Transactions_Over_Time', 'Contract_SMS_Transactions', 'Contract_SMS_Transactions_Over_Time',
  'Contract_Rule_Evaluation_Transactions', 'Contract_Rule_Execution_Transactions',
  'Contract_Rule_Evaluation_Transactions_Over_Time', 'Contract_Rule_Execution_Transactions_Over_Time'
];

let us_field_dp_metering_oems;
if (process.env.REACT_APP_USFIELD_DP_METERING_OEMS) {
  us_field_dp_metering_oems = process.env.REACT_APP_USFIELD_DP_METERING_OEMS.split(", ");
}
let eu_field_dp_metering_oems;
if (process.env.REACT_APP_EU_DP_METERING_OEMS) {
  eu_field_dp_metering_oems = process.env.REACT_APP_EU_DP_METERING_OEMS.split(", ");
}

const getData = async (accessToken, reportName, args, signal) => {
  let params = {};
  let response = null;
  let error = {};

  if (reportName.startsWith('Contract_Metering_')) {
    reportName = getMeteringReportName(reportName);
    params = {
      ...args,
      region_name: getMeteringRegion(args),
      dsn: 'All',
      year: undefined,
      duration: undefined,
      period: undefined,
    };
  } else if (
    isAvgDailyTxPerDevices(reportName) &&
    (
      (us_field_dp_metering_oems.includes(args.oem_id) && args.region_name === 'US-FIELD') ||
      (eu_field_dp_metering_oems.includes(args.oem_id) && args.region_name === 'EU') ||
      (args.oem_id === '39a9391a' && ['US-VPC', 'EU'].includes(args.region_name))
    )
  ) {
    const avgDailyTxParams = getAvgDailyTxParams(reportName, args);
    reportName = avgDailyTxParams.reportName;
    params = {
      ...args,
      ...avgDailyTxParams,
      reportName: undefined,
      year: undefined,
      duration: undefined,
      period: undefined,
    };
  } else if (['Total_Devices_Activated_Till_Date', 'Total_Users_Activated_Till_Date'].includes(reportName)) {
    reportName = reportName === 'Total_Devices_Activated_Till_Date'
      ? 'Activated_Devices'
      : 'User_Analysis_Total_Users';
    params = {
      ...args,
      start_date: '2000-01-01',
      end_date: moment().format('YYYY-MM-DD'),
      year: undefined,
      duration: undefined,
      period: undefined,
    };
  } else if (reportName === 'Download_Device_Report') {
    params = billingReportsDataParams(args);
    reportName = params.duration === 'Monthly'
      ? 'Get_Monthly_Billing_Report_Data'
      : 'Get_Quarterly_Billing_Report_Data';
  } else {
    // Remove unused attributes as the reports service API does not use them
    params = {
      ...args,
      dsns: undefined,
      year: undefined,
      duration: undefined,
      period: undefined,
    };
  }

  const argString = [
    `report_name=${reportName}`,
    ...Object.keys(params)
      .map((key) => {
        if (Array.isArray(params[key])) {
          return `${key}=${params[key].join('%7C%7C')}`;
        }
        return params[key] ? `${key}=${params[key]}` : '';
      }),
  ]
    .filter(Boolean)
    .join('&');

  const urlBase = process.env.REACT_APP_REPORTS_SERVICE_URL;
  const url =
    MeteringApiReportNames.includes(reportName) &&
    (
      (us_field_dp_metering_oems.includes(params.oem_id) && params.region_name === 'USFIELD') ||
      (eu_field_dp_metering_oems.includes(params.oem_id) && params.region_name === 'EUFIELD') ||
      (params.oem_id === '39a9391a' && params.region_name === 'SHARKUE1')
    )
      ? `${process.env.REACT_APP_METERING_SERVICE_URL}/reportsdata?${argString}`
      : `${urlBase}/reportsdata?${argString}`;

  try {
    response = await fetch(url, {
      signal,
      headers: {
        Authorization: `auth_token ${accessToken}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });

    if (!response.ok) throw response;

    const contentType = response.headers.get('Content-Type');
    return contentType && contentType.includes('application/json')
      ? await response.json()
      : await response.text();
  } catch (err) {
    error.message = 'Alb timeout error';
    throw error;
  }
};


const isAvgDailyTxPerDevices = (reportName) => {
  const relevantReports = [
    'Contract_Quarter_To_Date_Transactions_Per_Device',
    'Contract_Quarter_To_Date_Datapoint_Transactions',
    'Contract_Previous_Quarter_Transactions_Per_Device',
    'Contract_Previous_Quarter_Datapoint_Transactions',
    'Contract_Two_Quarters_Ago_Transactions_Per_Device',
    'Contract_Two_Quarters_Ago_Datapoint_Transactions'
  ];

  return relevantReports.includes(reportName);
};

const getAvgDailyTxParams = (reportName, params) => {
  const avg_daily_tx_dates = getAvgDailyTxDates();

  const reportMappings = {
    'Contract_Quarter_To_Date_Transactions_Per_Device': 'Contract_Active_Devices',
    'Contract_Quarter_To_Date_Datapoint_Transactions': 'Contract_Datapoint_Transactions',
    'Contract_Previous_Quarter_Transactions_Per_Device': 'Contract_Active_Devices',
    'Contract_Previous_Quarter_Datapoint_Transactions': 'Contract_Datapoint_Transactions',
    'Contract_Two_Quarters_Ago_Transactions_Per_Device': 'Contract_Active_Devices',
    'Contract_Two_Quarters_Ago_Datapoint_Transactions': 'Contract_Datapoint_Transactions'
  };

  const dateMappings = {
    'Contract_Quarter_To_Date_Transactions_Per_Device': {
      start_date: avg_daily_tx_dates.cur_quarter_start_date,
      end_date: avg_daily_tx_dates.cur_quarter_end_date
    },
    'Contract_Quarter_To_Date_Datapoint_Transactions': {
      start_date: avg_daily_tx_dates.cur_quarter_start_date,
      end_date: avg_daily_tx_dates.cur_quarter_end_date
    },
    'Contract_Previous_Quarter_Transactions_Per_Device': {
      start_date: avg_daily_tx_dates.prev_quarter_start_date,
      end_date: avg_daily_tx_dates.prev_quarter_end_date
    },
    'Contract_Previous_Quarter_Datapoint_Transactions': {
      start_date: avg_daily_tx_dates.prev_quarter_start_date,
      end_date: avg_daily_tx_dates.prev_quarter_end_date
    },
    'Contract_Two_Quarters_Ago_Transactions_Per_Device': {
      start_date: avg_daily_tx_dates.two_quarters_ago_start_date,
      end_date: avg_daily_tx_dates.two_quarters_ago_end_date
    },
    'Contract_Two_Quarters_Ago_Datapoint_Transactions': {
      start_date: avg_daily_tx_dates.two_quarters_ago_start_date,
      end_date: avg_daily_tx_dates.two_quarters_ago_end_date
    }
  };

  const updatedParams = {
    reportName: reportMappings[reportName],
    ...dateMappings[reportName],
    dsn: reportName.includes('Datapoint_Transactions') ? 'All' : null,
    region_name: getRegionName(reportName, params)
  };

  return updatedParams;
};

const getAvgDailyTxDates = () => {
  const date = new Date();
  const today = moment(date).format('YYYY-MM-DD');
  const quarter = Math.floor(date.getMonth() / 3);

  const formatStartDate = (monthOffset) =>
    moment(new Date(date.getFullYear(), quarter * 3 + monthOffset, 1)).format('YYYY-MM-DD');

  const formatEndDate = (startDate) =>
    moment(new Date(startDate.getFullYear(), startDate.getMonth() + 3, 0)).format('YYYY-MM-DD');

  const cur_quarter_start_date = formatStartDate(0);
  const prev_quarter_start_date = formatStartDate(-3);
  const prev_quarter_end_date = formatEndDate(new Date(date.getFullYear(), quarter * 3 - 3, 1));
  const two_quarters_ago_start_date = formatStartDate(-6);
  const two_quarters_ago_end_date = formatEndDate(new Date(date.getFullYear(), quarter * 3 - 6, 1));

  return {
    cur_quarter_start_date,
    cur_quarter_end_date: today,
    prev_quarter_start_date,
    prev_quarter_end_date,
    two_quarters_ago_start_date,
    two_quarters_ago_end_date
  };
};

const getMeteringRegion = (params) => {
  if (params.region_name === 'US-VPC' && params.oem_id === '39a9391a') {
    return 'SHARKUE1';
  }
  return params.region_name === 'EU' ? 'EUFIELD' : 'USFIELD';
};

const getMeteringReportName = (reportName) => {
  const reportNameMap = {
    'Contract_Metering_Datapoint_Transactions': 'Contract_Datapoint_Transactions',
    'Contract_Metering_Datapoint_Transactions_Over_Time': 'Contract_Datapoint_Transactions_Over_Time',
    'Contract_Metering_Total_Transactions_Over_Time': 'Contract_Total_Transactions_Over_Time',
    'Contract_Metering_Datastream_Transactions': 'Contract_Datastream_Transactions',
    'Contract_Metering_Datastream_Transactions_Over_Time': 'Contract_Datastream_Transactions_Over_Time',
    'Contract_Metering_SMS_Transactions': 'Contract_SMS_Transactions',
    'Contract_Metering_SMS_Transactions_Over_Time': 'Contract_SMS_Transactions_Over_Time',
    'Contract_Metering_Rule_Evaluation_Transactions': 'Contract_Rule_Evaluation_Transactions',
    'Contract_Metering_Rule_Execution_Transactions': 'Contract_Rule_Execution_Transactions',
    'Contract_Metering_Rule_Evaluation_Transactions_Over_Time': 'Contract_Rule_Evaluation_Transactions_Over_Time',
    'Contract_Metering_Rule_Execution_Transactions_Over_Time': 'Contract_Rule_Execution_Transactions_Over_Time'
  };

  return reportNameMap[reportName] || null;
};

const getRegionName = (regionName, params) =>
  ['Contract_Quarter_To_Date_Datapoint_Transactions',
    'Contract_Previous_Quarter_Datapoint_Transactions',
    'Contract_Two_Quarters_Ago_Datapoint_Transactions']
    .includes(regionName) ? getMeteringRegion(params) : params.region_name;

// ====================================
// REPORTS
// ====================================

export const getReport = async (accessToken, {
  name,
  args,
  handler
}, signal) => getData(accessToken, name, args, signal)
  .then(handler)
  .catch((error) => {
    if(name.includes('Contract') || error.message === 'Alb timeout error') {
      error = {};
      error.message = t('unableToFetchData');
      return error;
    } else {
      throw error;
    }
  });

export const getReportsDescription = async (accessToken,
  { name }) => getReportsMetadata(accessToken, name);

const report = (name, alias = name, roles = Consts.ALL_ALLOWED_ROLES) => handler => args => ({
  alias,
  args,
  handler,
  name,
  roles,
});

// ===================================
// RESPONSE TRANSFORMATIONS
// ===================================

function groupBy(entries, key) {
  return entries.reduce((current, entry) => {
    const accumulator = current;
    (accumulator[entry[key]] = accumulator[entry[key]] || []).push(entry);
    return accumulator;
  }, {

  });
}

export function asArrList() {
  return entries => [{
    values: entries.map(({ values }) => (values)),
  }];
}

export function as1D(x) {
  return ([entry]) => {
    let retVal = entry[x];
    if (! Number.isNaN (parseInt(retVal))) {
      const val = Number(entry[x].replace(/,/g, ''));
      if (val === 0) {
        retVal = val;
      } else if (val > 0 && val < 1) {
        retVal = val.toFixed(2);
      } else {
        retVal = Math.round(val);
      }
    }
    // toString() is used as number - 0 is getting treated as false so ValueDisplayReport
    // function is showing loader infinitely
    return retVal.toString();
  };
}

export function as2D(x, y) {
  return entries => [{
    values:
      entries.map(entry => ({
        x: ! entry[x] ? '' : entry[x],
        y: ! entry[y] ? 0 : isNaN(entry[y]) ? Number(entry[y].replace(/,/g, '')) : Number(entry[y].toString().replace(/,/g, '')),
      })),
  }];
}

export function asKeyValuePair(key, value) {
  return entries => entries.map(entry => ({
    id: entry[key],
    name: entry[value],
  }));
}

export function as3D(x, y, z) {
  return (entries) => {
    const groupedEntries = groupBy(entries, z);
    const allXs = entries
      .reduce((xs, entry) => (xs.indexOf(entry[x]) < 0 ? [...xs, entry[x]] : xs), []);

    // before sort
    // allXs = ["2015-02", "2015-03", "2015-04", "2015-05", "2015-06",
    //   "2015-07", "2015-08", "2015-09", "2015-11", "2015-12", "2016-01",
    //   "2016-02", "2016-03", "2016-04", "2016-05", "2016-06", "2016-07",
    //   "2016-08", "2016-09", "2016-10", "2016-11", "2016-12", "2017-01",
    //   "2017-02", "2017-03", "2017-04", "2017-05", "2017-06", "2017-07",
    //   "2017-08", "2017-09", "2017-10", "2017-11", "2017-12", "2018-04",
    //   "2018-06", "2018-07", "2015-10"];

    allXs.sort((a, b) => {
      if (Number(a.split('-')[0]) < Number(b.split('-')[0])) return -1;
      if (Number(a.split('-')[0]) > Number(b.split('-')[0])) return 1;
      if (Number(a.split('-')[1]) < Number(b.split('-')[1])) return -1;
      if (Number(a.split('-')[1]) > Number(b.split('-')[1])) return 1;
      return 0;
    });

    // after sort
    // allxs = ["2015-02", "2015-03", "2015-04", "2015-05",
    //     "2015-06", "2015-07", "2015-08", "2015-09", "2015-10",
    //     "2015-11", "2015-12", "2016-01", "2016-02", "2016-03",
    //     "2016-04", "2016-05", "2016-06", "2016-07", "2016-08",
    //     "2016-09", "2016-10", "2016-11", "2016-12", "2017-01",
    //     "2017-02", "2017-03", "2017-04", "2017-05", "2017-06",
    //     "2017-07", "2017-08", "2017-09", "2017-10", "2017-11",
    //     "2017-12", "2018-04", "2018-06", "2018-07"]

    return keys(groupedEntries).map(label => ({
      label,
      values: allXs.map((xValue) => {
        const entry = groupedEntries[label].find(groupedEntry => groupedEntry[x] === xValue);
        return {
          x: ! xValue ? '' : xValue,
          y: entry ? Number(entry[y]) : 0,
        };
      }),
    }));
  };
}

export function filteredWithCombinedData(handler, forLabel = '') {
  return (entries) => {
    let max = 0;
    let data = entries.map((e) => {
      e.total = (e.total && typeof(parseInt(e.total, 10)) !== 'undefined') ? parseInt(e.total, 10) : 0;
      if (e.total > max) max = e.total;
      return e;
    });
    const combinedJunkData = data.filter(e => e.total < max * Consts.PERCENTAGE_TO_HIDE);
    const otherData = {

    };
    otherData[forLabel] = `other ${combinedJunkData.length} values`;
    otherData.total = combinedJunkData.length > 0
      ? combinedJunkData.reduce((accumulator, value) => ({
        total: accumulator.total + value.total,
      })).total : 0;
    data = data.filter(e => e.total > max * Consts.PERCENTAGE_TO_HIDE);
    if (combinedJunkData.length > 0) data.push(otherData);
    return handler(data);
  };
}

export function filtered(handler) {
  return (entries) => {
    let max = 0;
    return handler(entries.map((e) => {
      e.total = (e.total && typeof(parseInt(e.total, 10)) !== 'undefined') ? parseInt(e.total, 10) : 0;
      if (e.total > max) max = e.total;
      return e;
    }).filter(e => e.total > max * Consts.PERCENTAGE_TO_HIDE));
  };
}

function defaultMarker(responseMarker) {
  if (responseMarker[0] && responseMarker[0].latitude && responseMarker[0].longitude) {
    return {
      lat: Number(responseMarker[0].latitude),
      lng: Number(responseMarker[0].longitude),
    };
  }
  return {
    lat: Number('38.4925191'),
    lng: Number('-96.1217629'),
  };
}


// =======================
// FILTERS
// =======================

export const contractlistOfOemModels = report('Contract_Get_List_Of_Oem_Models')(asArrList());

export const listOfOemModels = report('Get_List_Of_Oem_Models')(asArrList());

export const listOfDataPoints = report('Get_List_Of_Datapoint_Names')(asArrList());

export const listOfNotificationTypes = report('Get_List_Of_Notification_Types')(asArrList());

export const listOfSwVersions = report('Get_List_Of_Ayla_Sw_Versions')(asArrList());

export const listOfRegions = report('Get_List_Of_Regions')(asArrList());

export const listOfOems = report(
  'Get_List_Of_Oems', 'Get_List_Of_Oems', Consts.AYLA_ALLOWED_ROLES,
)(
  asKeyValuePair('oem_id', 'oem_name'),
);

// =========================
// OVERVIEW
// =========================

export const activatedDevices = report('Activated_Devices')(as1D('total'));

export const registeredDevices = report('Percentage_Of_Registered_Devices')(as1D('percentage'));

export const activeDevices = report('Percentage_Of_Active_Devices')(as1D('percentage'));

export const devicesOnlineToday = report('Devices_Online_Today')(response => response.total);

export const totalUsers = report('Total_Users')(as1D('total'));

export const usersWithDevices = report('Percentage_Of_Users_With_Devices')(as1D('percentage'));

export const totalDatapoints = report('Datapoint_Transmissions')(as1D('total'));

export const deviceDatapointsTransmissions = report('Device_Datapoint_Transmissions')(as1D('total'));

export const deviceActivation = report('New_Device_Activations_Over_Time')(as2D('period', 'total'));

export const usersWithRegisteredDevices = report('New_User_Registration_Over_Time')(as2D('period', 'total'));

export const totalDataTransmissionOverTime = report('Total_Data_Transmission_Over_Time')(as2D('period', 'total'));

export const totalDevicesActivatedTillDate = report('Total_Devices_Activated_Till_Date')(as1D('total'));

export const totalUsersActivatedTillDate = report('Total_Users_Activated_Till_Date')(as1D('total'));

// ===============================
// ONBOARDING ANALYSIS
// ===============================

export const activatedDevicesLeft = report(
  'Onboarding_Analysis_Activated_Devices', 'Onboarding_Analysis_Activated_Devices_Left',
)(as1D('total'));

export const activatedDevicesRight = report(
  'Onboarding_Analysis_Activated_Devices', 'Onboarding_Analysis_Activated_Devices_Right',
)(as1D('total'));

export const percentageOfRegisteredDevicesLeft = report(
  'Onboarding_Analysis_Percentage_Registered_Devices', 'Percentage_Registered_Left',
)(as1D('percentage'));

export const percentageOfRegisteredDevicesRight = report(
  'Onboarding_Analysis_Percentage_Registered_Devices', 'Percentage_Registered_Right',
)(as1D('percentage'));

export const percentageOfActiveDevicesLeft = report(
  'Onboarding_Analysis_Percentage_Active', 'Percentage_Active_Left',
)(as1D('percentage'));

export const percentageOfActiveDevicesRight = report(
  'Onboarding_Analysis_Percentage_Active', 'Percentage_Active_Right',
)(as1D('percentage'));

export const averageDaysToActivationLeft = report(
  'Average_Days_To_Activation', 'Average_Days_To_Activation_Left',
)(as1D('avg'));

export const averageDaysToActivationRight = report(
  'Average_Days_To_Activation', 'Average_Days_To_Activation_Right',
)(as1D('avg'));

// ================================
// DEVICE TRENDS
// ================================

export const totalActivatedDevicesOverTime = report('Activated_Over_Time')(as2D('period', 'total'));

export const totalRegisteredDevicesOverTime = report('Registered_Over_Time')(as2D('period', 'total'));

export const deviceInactivity = report('Device_Inactivity')(as2D('period', 'total'));

export const provisionedDevicesOverTime = report('Provisioned_Devices_Over_Time')(as2D('period', 'total'));

export const registeredDevicesOverTime = report(
  'Percentage_Of_Registered_Devices_Over_Time',
)(as2D('period', 'percentage'));

export const activeDevicesOverTime = report('Active_Devices_Over_Time')(as2D('period', 'total'));

export const averageOfflineDeviceEvents = report(
  'Average_Offline_Events_Analysis_Over_Time',
)(as3D('period', 'avg', 'event'));

export const deviceDistribution = report('Device_Distribution')(response => response.map(marker => ({
  id: `${marker.latitude}${marker.longitude}`,
  lat: Number(marker.latitude),
  lng: Number(marker.longitude),
  total: Number(marker.total),
})));

// ==============================
// DEVICE SW ANALYSIS
// ==============================

export const aylaSoftwareVersionOverTime = report(
  'Ayla_Module_Version_Updated_Over_Time',
)(filtered(as3D('period', 'total', 'sw_ver')));

export const userCountByAylaSoftwareVersion = report(
  'User_Count_By_Ayla_Module',
)(filteredWithCombinedData(as2D('sw_ver', 'total'), 'sw_ver'));

export const deviceCountByAylaSoftwareVersion = report(
  'Device_Count_By_Ayla_Module',
)(filteredWithCombinedData(as2D('sw_ver', 'total'), 'sw_ver'));

export const hostSoftwareVersionOverTime = report(
  'Host_Module_Version_Updated_Over_Time',
)(as3D('period', 'total'));

export const userCountByHostSoftwareVersion = report(
  'User_Count_By_Host_SW',
)(filteredWithCombinedData(as2D('dp_value', 'total'), 'dp_value'));

export const deviceCountByHostSoftwareVersion = report(
  'Device_Count_By_Host_SW',
)(filteredWithCombinedData(as2D('dp_value', 'total'), 'dp_value'));

// ===============================
// DEVICE USAGE ANALYSIS
// ===============================

export const averageDailyUsageByStatus = report('Average_Daily_Transmissions_By_Status_Over_Time')(response => [
  {
    label: 'total',
    ...as2D('period', 'avg_unreg')(response)[0],
  },
  {
    label: 'registered',
    ...as2D('period', 'avg_reg')(response)[0],
  },
]);

export const averageDailyUsageByDevice = report('Average_Daily_Transmissions_Per_Device')((response) => ({
  any_device: as1D('avg_total')(response),
  reg: as1D('avg_reg')(response),
  unreg: as1D('avg_unreg')(response),
}));

export const dailyTransmissionsByDevice = report('Daily_Transmissions_By_Device')(response => response);

// =========================
// DEVICE DETAILS
// =========================

export const datapointCountByValueOverTime = report('Device_Connectivity_Events_By_Time')(response => [
  {
    label: 'derived_offline',
    values: response.map(({ period, total_derived_offline }) => ({
      x: period,
      y: Number(total_derived_offline),
    })),
  },
  {
    label: 'initializing',
    values: response.map(({ period, total_initializing }) => ({
      x: period,
      y: Number(total_initializing),
    })),
  },
  {
    label: 'offline',
    values: response.map(({ period, total_offline }) => ({
      x: period,
      y: Number(total_offline),
    })),
  },
  {
    label: 'online',
    values: response.map(({ period, total_online }) => ({
      x: period,
      y: Number(total_online),
    })),
  },
  {
    label: 'pollingonline',
    values: response.map(({ period, total_pollingonline }) => ({
      x: period,
      y: Number(total_pollingonline),
    })),
  },
]);


export const deviceAlertsSentByTime = report('Notifications_Sent_Over_Time')(as2D('period', 'total'));

export const deviceTransactionsCreatedByTime = report('Device_Transactions_Created_Over_Time')(as2D('period', 'total'));

export const deviceDailyTransactionTrend = report('Daily_Transaction_Trend_Over_Time')(as2D('period', 'avg'));

export const deviceLocation = report('Device_Location')(defaultMarker);

// ====================
// USER ANALYSIS
// ====================

export const totalUsersWithDevices = report('User_Analysis_Total_Users')(as1D('total'));

export const usersActivityByLastSignIn = report('User_Activity_By_Last_Sign_In_Over_Time')(as2D('period', 'total'));

export const usersCreatedOverTime = report('Users_Created_Over_Time')(as2D('period', 'total'));

// =============================
// NOTIFICATION ANALYSIS
// =============================
function filterNotificationResponse(response) {
  let max = 0;
  response.forEach((e) => {
    e.total = parseInt(e.total_push_android, 10)
      + parseInt(e.total_push_ios, 10)
      + parseInt(e.total_push_android_fcm, 10);
    if (e.total > max) max = e.total;
  });
  return response.filter(e => e.total > max * Consts.PERCENTAGE_TO_HIDE);
}

export const devicesAlertSubscriptionByModel = report('Notification_Subscription_By_Model')((response) => {
  const entries = filterNotificationResponse(response);
  return [
    {
      label: 'push_ios',
      values: entries.map(({ oem_model, total_push_ios }) => ({
        x: oem_model,
        y: Number(total_push_ios),
      })),
    },
    {
      label: 'push_android',
      values: entries.map(({ oem_model, total_push_android }) => ({
        x: oem_model,
        y: Number(total_push_android),
      })),
    },
    {
      label: 'push_android_fcm',
      values: entries.map(({ oem_model, total_push_android_fcm }) => ({
        x: oem_model,
        y: Number(total_push_android_fcm),
      })),
    },
  ];
});

export const devicesNotificationsSentByModel = report('Notifications_Sent_By_Model')((response) => {
  const entries = filterNotificationResponse(response);
  return [
    {
      label: 'push_ios',
      values: entries.map(({ oem_model, total_push_ios }) => ({
        x: oem_model,
        y: Number(total_push_ios),
      })),
    },
    {
      label: 'push_android',
      values: entries.map(({ oem_model, total_push_android }) => ({
        x: oem_model,
        y: Number(total_push_android),
      })),
    },
    {
      label: 'push_android_fcm',
      values: entries.map(({ oem_model, total_push_android_fcm }) => ({
        x: oem_model,
        y: Number(total_push_android_fcm),
      })),
    },
  ];
});

export const devicesNotificationSubscriptionByPropertyName = report(
  'Notification_Subscriptions_By_Property_Name',
)((response) => {
  const entries = filterNotificationResponse(response);
  return [
    {
      label: 'push_ios',
      values: entries.map(({ dp_value, total_push_ios }) => ({
        x: dp_value,
        y: Number(total_push_ios),
      })),
    },
    {
      label: 'push_android',
      values: entries.map(({ dp_value, total_push_android }) => ({
        x: dp_value,
        y: Number(total_push_android),
      })),
    },
    {
      label: 'push_android_fcm',
      values: entries.map(({ dp_value, total_push_android_fcm }) => ({
        x: dp_value,
        y: Number(total_push_android_fcm),
      })),
    },
  ];
});

export const devicesNotificationSentByPropertyName = report('Notifications_Sent_By_Property_Name')((response) => {
  const entries = filterNotificationResponse(response);
  return [
    {
      label: 'push_ios',
      values: entries.map(({ dp_value, total_push_ios }) => ({
        x: dp_value,
        y: Number(total_push_ios),
      })),
    },
    {
      label: 'push_android',
      values: entries.map(({ dp_value, total_push_android }) => ({
        x: dp_value,
        y: Number(total_push_android),
      })),
    },
    {
      label: 'push_android_fcm',
      values: entries.map(({ dp_value, total_push_android_fcm }) => ({
        x: dp_value,
        y: Number(total_push_android_fcm),
      })),
    },
  ];
});

// ===================================
// DATAPOINT TRANSMISSION TRENDS
// ===================================

export const datapointsTotalTransmissionsOverTime = report(
  'Total_Transmissions_Over_Time',
)(as3D('period', 'total', 'oem_model'));

export const datapointsAverageTransmissionsPerDevice = report(
  'Average_Transmissions_Per_Device_Over_Time',
)(as2D('period', 'avg'));

// ====================================
// DATAPOINT ANALYSIS
// ====================================

export const totalTransmissions = report('Total_Transmissions')(as1D('total'));

export const averageDailyTransmissions = report('Average_Daily_Transmissions')(as1D('avg'));

export const datapointCountByValue = report(
  'Datapoint_Count_By_Value',
)(filteredWithCombinedData(as2D('dp_value', 'total'), 'dp_value'));

export const datapointCountOverTime = report(
  'Datapoint_Count_Over_Time',
)(as2D('period', 'total'));

export const deviceCountByValue = report(
  'Device_Count_By_Value',
)(filteredWithCombinedData(as2D('dp_value', 'total'), 'dp_value'));

export const deviceCountByValueOverTime = report('Device_Count_By_Value_Over_Time')(as2D('period', 'total'));

// ================================
// METERED USAGE
// ================================

export const transactionByTypeDuringTransactionPeriod = report(
  'Contract_Transaction_By_Type_During_Contract_Period',
)(response => [
  {
    label: 'Datapoint Transactions',
    ...as2D('period', 'transaction_count')(response)[0],
  },
  {
    label: 'Data Streaming Transactions',
    ...as2D('period', 'dss_count')(response)[0],
  },
]);

export const numberOfTransactionDuringContractOverTime = report(
  'Contract_Number_Of_Transaction_During_Contract_Period_Over_Time',
)((response) => {
  const entries = response;
  return [
    {
      label: 'Current Usage',
      values: entries.map(({ period, current_usage }) => ({
        x: period,
        y: Number(current_usage),
      })),
    },
    {
      label: 'Available Transaction',
      values: entries.map(({ period, purchased_transactions }) => ({
        x: period,
        y: Number(purchased_transactions),
      })),
    },
  ];
});

export const deviceActiveDuringContractTerm = report(
  'Contract_Devices_Active_During_Contract_Term',
)(as1D('total'));

export const averageDailyTransactionActiveDevice = report(
  'Contract_Average_Daily_Transaction_Per_Active_Device',
)(as1D('avg'));

export const transactionUsed = report('Contract_Transaction_Used')(as1D('transactionUsed'));

export const averageAnnualTransactionPerActiveDevice = report(
  'Contract_Average_Annual_Transaction_Per_Active_Device',
)(as1D('avg'));

// ================================
// METERING - DEVICES PAGE
// ================================
export const activeDevice = report('Contract_Active_Devices')(as1D('total'));
export const dsnReservedOverTime = report('Contract_Dsn_Reserved_Over_Time') (as2D('period', 'total'));
export const devicesProvisionedOverTime = report('Contract_Devices_Provisioned_Over_Time') (as2D('period', 'total'));
export const devicesActivatedOverTime = report('Contract_Devices_Activated_Over_Time') (as2D('period', 'total'));
export const dsnReserved = report('Contract_Dsn_Reserved')(as1D('total'));
export const devicesProvisioned = report('Contract_Devices_Provisioned')(as1D('total'));
export const devicesActivated = report('Contract_Devices_Activated')(as1D('total'));
export const transactionsPerDeviceQuarterToDate = report(
  'Contract_Transactions_Per_Device_Quarter_To_Date'
)(as1D('avg'));
export const transactionsPerDevicePreviousQuarter = report(
  'Contract_Transactions_Per_Device_Previous_Quarter'
)(as1D('avg'));
export const transactionsPerDeviceTwoQuartersAgo = report(
  'Contract_Transactions_Per_Device_Two_Quarters_Ago'
)(as1D('avg'));

// ================================
// METERING - DEVICES PAGE(AVERAGE DAILY TRANSACTIONS PER DEVICE)
// ================================
export const quarterToDateTransactionsPerDevice = report(
  'Contract_Quarter_To_Date_Transactions_Per_Device'
)(as1D('total'));
export const quarterToDateTotalTransactions = report(
  'Contract_Quarter_To_Date_Datapoint_Transactions')((response) =>({
  batch_datapoint: as1D('batch_datapoint')(response),
  single_datapoint: as1D('single_datapoint')(response),
  total: as1D('total')(response),
  total_datapoint: as1D('total_datapoint')(response),
}));

export const previousQuarterTransactionsPerDevice = report(
  'Contract_Previous_Quarter_Transactions_Per_Device'
)(as1D('total'));
export const previousQuarterTotalTransactions = report(
  'Contract_Previous_Quarter_Datapoint_Transactions')((response) =>({
  batch_datapoint: as1D('batch_datapoint')(response),
  single_datapoint: as1D('single_datapoint')(response),
  total: as1D('total')(response),
  total_datapoint: as1D('total_datapoint')(response),
}));

export const twoQuartersAgoTransactionsPerDevice = report(
  'Contract_Two_Quarters_Ago_Transactions_Per_Device'
)(as1D('total'));
export const twoQuartersAgoTotalTransactions = report(
  'Contract_Two_Quarters_Ago_Datapoint_Transactions')((response) =>({
  batch_datapoint: as1D('batch_datapoint')(response),
  single_datapoint: as1D('single_datapoint')(response),
  total: as1D('total')(response),
  total_datapoint: as1D('total_datapoint')(response),
}));

// ================================
// METERING - TRANSACTIONS PAGE
// ================================
export const datapointTransactionsOverTime = report('Contract_Datapoint_Transactions_Over_Time')(as2D('period', 'total'));
export const datastreamTransactionsOverTime = report('Contract_Datastream_Transactions_Over_Time')(as2D('period', 'total'));
export const transactionsOverTime = report(
  'Contract_Total_Transactions_Over_Time',
)(response => [
  {
    label: 'Datapoint Transactions',
    ...as2D('period', 'transaction_count')(response)[0],
  },
  {
    label: 'Datastream Transactions',
    ...as2D('period', 'dss_count')(response)[0],
  },
]);
export const totalTransactions = report('Contract_Total_Transactions')(as1D('total'));
export const datapointTransactions = report('Contract_Datapoint_Transactions')(as1D('total'));
export const datastreamTransactions = report('Contract_Datastream_Transactions')(as1D('total'));

// ================================
// METERING - NEW APIs
// ================================
export const totalTransactionsOverTime = report('Contract_Metering_Total_Transactions_Over_Time')(as2D('period', 'total'));
export const contractDatapointsTransactionsOverTime = report(
  'Contract_Metering_Datapoint_Transactions_Over_Time',
)(response => [
  {
    label: 'Batch Datapoints',
    ...as2D('period', 'batch_datapoint')(response)[0],
  },
  {
    label: 'Single Datapoints',
    ...as2D('period', 'single_datapoint')(response)[0],
  },
]);

export const contractDatastreamTransactionsOverTime =
    report('Contract_Metering_Datastream_Transactions_Over_Time')(as2D('period', 'total_datastream'));

export const contractTotalDatapointsTransactions = report(
  'Contract_Metering_Datapoint_Transactions')((response) =>({
    batch_datapoint: as1D('batch_datapoint')(response),
    single_datapoint: as1D('single_datapoint')(response),
    total: as1D('total')(response),
    total_datapoint: as1D('total_datapoint')(response),
  }));

export const contractTotalDatastreamTransactions = report(
    'Contract_Metering_Datastream_Transactions')((response) =>({
  stream_registration: as1D('stream_registration')(response),
  stream_datapointack: as1D('stream_datapointack')(response),
  stream_location: as1D('stream_location')(response),
  stream_connection: as1D('stream_connection')(response),
  stream_datapoint: as1D('stream_datapoint')(response),
  stream_activation: as1D('stream_activation')(response),
  total_datastream: as1D('total_datastream')(response),
  total: as1D('total')(response),
}));

export const contractSmsTransactions = report('Contract_Metering_SMS_Transactions')(response => ({
  sms: as1D('sms')(response),
  total: as1D('total')(response),
}));

export const contractSmsTransactionsOverTime = report('Contract_Metering_SMS_Transactions_Over_Time')(
  as2D('period', 'sms')
);

export const contractRuleEvaluationTransactions = report(
    'Contract_Metering_Rule_Evaluation_Transactions')(response => ({
  rule_evaluation: as1D('rule_evaluation')(response),
  total: as1D('total')(response),
}));

export const contractRuleEvaluationTransactionsOverTime = report('Contract_Metering_Rule_Evaluation_Transactions_Over_Time')(
    as2D('period', 'rule_evaluation')
);

export const contractRuleExecutionTransactions = report(
    'Contract_Metering_Rule_Execution_Transactions')(response => ({
  rule_execution: as1D('rule_execution')(response),
  total: as1D('total')(response),
}));

export const contractRuleExecutionTransactionsOverTime = report('Contract_Metering_Rule_Execution_Transactions_Over_Time')(
    as2D('period', 'rule_execution')
);

// Billing Reports Data
export const downloadDeviceReport = report('Download_Device_Report')();